import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { ORG_ADMIN_DASHBOARD_QUERY_KEY } from "@/hooks/queries/useOrgAdminDashboard";
import { ORG_ADMIN_PULSE_QUERY_KEY } from "@/hooks/queries/useOrgAdminPulse";
import type {
  CreateOrgInvitationRequest,
  CreatedOrgInvitationData,
} from "@/hooks/queries/org-invitation-types";

// POST /org-admin/invitations (Story 14.2, FR75) — the org_admin "Add person"
// invitation. On 201 the server has consumed a seat and queued the invitation
// email, so success invalidates the org_admin dashboard so the seat pool
// (consumed/remaining) and the roster refetch with the new clinician.
//
// The response is PHI-free (no name/email echoed) and the screen navigates back
// to the dashboard on success, so there is nothing sensitive to cache here.
// `skipAuthRefresh` is OMITTED — a post-session data call that rides the
// silent-refresh path.

export function useCreateOrgInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateOrgInvitationRequest) =>
      apiFetch<CreatedOrgInvitationData>("/org-admin/invitations", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      // Prefix match: invalidates the dashboard's seat pool + roster so the new
      // seat consumption and the invitee show without a manual refresh.
      void queryClient.invalidateQueries({ queryKey: ORG_ADMIN_DASHBOARD_QUERY_KEY });
      // …and the Pulse panel beside it (PR #741 review). SIBLING keys do not
      // prefix-match: `["org-admin","dashboard"]` never reaches
      // `["org-admin","pulse"]`. `org_admin.clinician.invited` is on the Pulse
      // allow-list and IS rendered, and Add Person navigates straight back to the
      // dashboard — so within the panel's staleTime the roster showed the new
      // clinician while the "last 7 days" panel beside it did not.
      void queryClient.invalidateQueries({ queryKey: ORG_ADMIN_PULSE_QUERY_KEY });
    },
  });
}
